import { useEffect, useState } from "react";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { PageHeader } from "@/shared/ui/PageHeader";
import { prettifyApiError } from "@/shared/api";
import { listSupplierCatalogs, type SupplierCatalogMeta } from "./api";
import { SUPPLIER_TABS } from "./components/suppliers";
import { SupplierTabs } from "./components/SupplierTabs";
import { SupplierAnagraficaCard } from "./components/SupplierAnagraficaCard";
import { WooSyncSection } from "./components/WooSyncSection";
import { BizzottoImportCard } from "./components/BizzottoImportCard";
import { SupplierCatalogSection } from "./components/SupplierCatalogSection";
import { WarehouseStockSection } from "./components/WarehouseStockSection";
import { RitiroAutomaticoCard } from "./components/RitiroAutomaticoCard";

// Pagina «Fornitori» UNIFICATA a tab (decisione Davide 2026-07-03): una tab
// per fornitore con dentro TUTTO — anagrafica, strumenti specifici (sync
// WooCommerce per Itamoby/Ityhome, import GSheet per Bizzotto), catalogo
// dimensioni/colli e prodotti Poleepo del brand. Sostituisce la vecchia
// coppia Fornitori + Catalogo (la voce Catalogo è uscita dal menu; /catalogo
// redirige qui). Questo file resta l'orchestratore: le sezioni vivono in
// ``components/``.

export function FornitoriPage() {
  const [activeId, setActiveId] = useState<string>(SUPPLIER_TABS[0]?.id ?? "itamoby");
  const [catalogMeta, setCatalogMeta] = useState<SupplierCatalogMeta[]>([]);
  const [metaError, setMetaError] = useState<string | null>(null);
  // Bump dopo l'import Bizzotto → la sezione catalogo rifetcha le entries.
  const [catalogReload, setCatalogReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void listSupplierCatalogs()
      .then((rows) => {
        if (!cancelled) setCatalogMeta(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setMetaError(prettifyApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tab = SUPPLIER_TABS.find((t) => t.id === activeId);
  const hasListino = catalogMeta.find((m) => m.supplier_id === tab?.id)?.has_listino ?? false;

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Fornitori"
        subtitle="Anagrafica, strumenti, catalogo e prodotti di ogni fornitore — una tab per fornitore."
        meta={
          <>
            Fornitori
            <br />
            <strong className="tabular-nums text-[11.5px] text-ink-2 tabular-nums">
              {SUPPLIER_TABS.length} attivi
            </strong>
          </>
        }
      />

      <SupplierTabs tabs={SUPPLIER_TABS} activeId={activeId} onSelect={setActiveId} />

      <ErrorBanner message={metaError} className="mb-3" />

      {tab && (
        // key={tab.id}: al cambio tab le sezioni si rimontano con stato
        // pulito (filtri, risultati sync, messaggi) — niente residui del
        // fornitore precedente.
        <div key={tab.id}>
          <SupplierAnagraficaCard def={tab} />

          <RitiroAutomaticoCard
            slot={tab.ritiro}
            nota={tab.ritiroNota}
            condiviso={tab.ritiroCondiviso}
          />

          {(tab.id === "itamoby" || tab.id === "ityhome") && (
            <WooSyncSection supplierId={tab.id} supplierLabel={tab.label} />
          )}

          {tab.id === "bizzotto" && (
            <BizzottoImportCard onImported={() => setCatalogReload((n) => n + 1)} />
          )}

          {tab.id === "magazzino" && <WarehouseStockSection />}

          {/* Il Magazzino non ha listino colli ne' brand Poleepo: le sezioni
              catalogo/prodotti non hanno senso per il fornitore interno. */}
          {tab.id !== "magazzino" && (
            <>
              {/* La griglia «Prodotti Poleepo» e' stata cancellata il
                  2026-09-02 (decisione Davide): mostrava il mirror fermo al
                  13/05, senza foto (0 righe su 14.913 avevano un'immagine) e
                  senza EAN (6 su 14.913). Il log di Caddy dice che in 30
                  giorni non l'ha aperta nessuno. I cloni SKU che ci vivevano
                  dentro stanno nel PIM dal 27/10 («Cloni d'ordine»). */}
              <SupplierCatalogSection
                supplierId={tab.id}
                hasListino={hasListino}
                reloadToken={catalogReload}
              />
            </>
          )}
        </div>
      )}
    </main>
  );
}
